import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import AutomationIcon from '@atlaskit/icon/core/automation';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expVal } from '@confluence/feature-experiments';

import { contentReviewerNudgeI18n } from '../i18n.ts';
import { markDiscoveryExperienceAsSeen } from '../utils/localStorage.ts';
import { resolveDynamicConfigStringValue } from '../utils/prompt-template.ts';

export const CONTENT_REVIEWER_NUDGE_ID = 'automation-discovery-content-reviewer-nudge';
const ROVO_CHAT_SOURCE = CONTENT_REVIEWER_NUDGE_ID;
const DYNAMIC_CONFIG_CONFIG_KEY = 'confluence-content-reviewer-discovery-prompt';

const DEFAULT_CONTENT_REVIEWER_PROMPT = `/build-automation

#### Trigger: \`PageStatusChangedTrigger\`
- Run whenever a Confluence page's status changes in the target Confluence space.
- Take the space from the context this chat is running in, namely the page or space the Rovo chat was opened from. It is always available there, so resolve it from that context and never stop to ask which space to use.
- Pass \`space\` as \`type: ARI\` when a Confluence space ARI is available (\`ari:cloud:confluence:<cloudId>:space/<spaceId>\`), otherwise as \`type: ID\` with the numeric space id. Both identify a space uniquely, and one of them is almost always available from the context.
- Treat \`type: NAME\` as a last resort, because space names are not unique and two spaces can share one.
- Never pass \`type: KEY\`, and never use the segment after \`/spaces/\` in a page URL as any kind of identifier. That segment is a URL alias rather than the space key, a KEY is looked up strictly as a key with no fallback, and passing the alias fails validation with \`TRANSFORMATION_FAILED\`. Searching for the alias does not work either: space search is semantic, and an opaque alias matches nothing.
- Scope the trigger to the single status \`Ready for review\`, so it fires on nothing else. The status must already be configured in that space, since content statuses are space-specific.
- Put the status filter on the trigger itself; do not add a separate condition after it.

#### Action 1: \`InvokeAgentAction\`
- **Agent:** Content Reviewer
- Adds the suggestions and writes the email body in one step.
- The agent emits progress narration ahead of its reply, such as \`Reviewing page...Preparing suggestions...Finalising suggestions...\`. That text is plain, never contains HTML tags, and always precedes the reply, so Action 2 strips it by cutting at the first \`<p>\`. The prompt below therefore requires the email to begin with \`<p>Hi\`.
- **Prompt:**

\`\`\`\`text
You review a Confluence page that has just been marked ready for review, add suggestions to it, then write a short HTML email about it.

Act immediately. Never ask questions, offer options, or explain what you'll do.

Recipient name (the person who moved the page to ready for review): {{page.editor.publicName}}

Page title: {{page.title}}

Page URL: {{page.url}}

PERMITTED ACTIONS

Fetch the page from the URL above, analyse it, and use your "Add suggestions to content" skill to add suggestions to it. That skill is the only write you may make, anywhere: never create, publish, move, archive, rename or delete anything, never change the page's status or labels, never rewrite the body yourself, and never accept your own suggestions, which are proposals for a human to act on.

Run the skill once. If it fails or adds nothing, write the email anyway, omitting the count sentence. Do not retry it, reword your suggestions to force a result, or fall back to editing the page or commenting on it.

Look nothing else up. The page is all you need.

THE LINK

The email needs a link that opens the page where your suggestions are visible, not a read-only view that hides them. The content you fetched tells you what kind of page it is, and different kinds are edited in different ways, so work the URL out yourself. Link only to this page and never invent a URL.

THE EMAIL

You are the recipient's review assistant, handing their draft back. Write in the first person about what you did, then point them at the page. The suggestions themselves live on the page: summarise their overall shape, but never reproduce individual suggestions, quote the wording you proposed, or list them one by one.

Never judge the writing. Do not say the draft is good, weak, nearly there or needs work, and never grade or score it. Describing what your suggestions focus on is fine; passing verdict on the author is not.

Write to the recipient alone. They moved the page to ready for review but may not have written it, so never name anyone else and never address a group.

Tone: warm, direct, plain, using contractions, no corporate filler. Never congratulate. Aim for 90 words and never exceed 130. No next steps, deadlines or offers of further help.

Reproduce the page title exactly wherever you use it; never paraphrase, shorten or reshape it.

STRUCTURE

Write these paragraphs in order, using only p, a and em tags. No lists, headings, other tags, inline CSS, markdown or code fences.

1. The greeting, alone in its paragraph: "Hi", a space, the recipient's first name, then a comma. The first name is the first word of the recipient name above. If that name is empty, write exactly <p>Hi,</p>

2. Two sentences. First: "Your draft of " then the exact page title then " is ready for you." Second: "As your review assistant, I went ahead and left " then the number of suggestions then " inline suggestions for " then the areas they cover, as two or three plain nouns joined naturally, for example flow, clarity and formatting. Take the number from the skill's own result and never estimate it. Take the areas from the suggestions you actually made rather than from that example, and never pad the list to three.

3. One or two sentences, 40 words at most, summarising what the suggestions add up to across the page as a whole: the recurring themes, and the sections most affected if a few stand out. Stay at the level of patterns. Never walk through suggestions individually, never quote replacement text, and never number them.

4. A single anchor, href set to the link described above and text "Review suggestions". It is the only link and the only call to action.

5. <em>Automated review by Content Reviewer Agent</em>

When the skill added exactly one suggestion, write paragraph 2 in the singular. When it reported no usable count, drop the number and write "some inline suggestions", keeping the rest of the sentence as it is. When it added nothing at all, replace paragraphs 2 and 3 with a single sentence saying the draft is ready for them and you found nothing to suggest, then continue with paragraphs 4 and 5 unchanged.

OUTPUT

The email must begin with the literal characters <p>Hi and end with the closing tag of paragraph 5. A later step finds the email by cutting your reply at its first <p>, so no HTML tag of any kind may appear before the greeting.

Write no commentary after the email: no summary of what you did, no description of the suggestions, no sign-off of your own.
\`\`\`\`

#### Action 2: \`SendTemplatedEmailAction\`
- Sends the email with bodyType=HTML.
- **To:** {{page.editor.emailAddress}} — the person who moved the page to ready for review, only
- **Subject:** Ready for review: {{page.title}}
- **Body:** \`<p>{{agentResponse.asString().substringAfter("<p>")}}\`
- Use that body exactly as written, ending with the closing brace. It is deliberately an unclosed \`<p>\`, so do not add a closing \`</p>\` to balance it: the agent's reply already closes every paragraph it opens, and a closing tag here lands after the final paragraph as a stray orphan.
- The expression drops the agent's progress narration by keeping only what follows the first \`<p>\`, then restores that opening tag. The narration is plain text and contains no tags, so the whole email survives intact.

#### Output
- One flat sequence: trigger, Action 1, Action 2. No nesting, branches or conditions.
- Create every component fully configured, with nothing left to add by hand in the flow editor.
`;

const CONTENT_REVIEWER_PROMPT_CONFIG = {
	configKey: DYNAMIC_CONFIG_CONFIG_KEY,
	fallback: DEFAULT_CONTENT_REVIEWER_PROMPT,
};

/**
 * Builds the Content Reviewer discovery nudge `ConversationAction` — its title, icon, the Rovo chat
 * it publishes on click, and the localStorage dismissal bookkeeping — given whether the nudge is
 * currently `enabled`.
 */
export const useContentReviewerNudgeAction = (isEnabled: boolean): ConversationAction | null => {
	const { formatMessage } = useIntl();
	const publishRovoChat = usePublish('ai-mate');

	const handleLocalStorageUpdate = useCallback(() => {
		markDiscoveryExperienceAsSeen(keys.AUTOMATION_DISCOVER_CONTENT_REVIEWER);
	}, []);

	return useMemo<ConversationAction | null>(
		() =>
			isEnabled && expVal('confluence-automations-content-reviewer-nudge', 'isEnabled', false)
				? {
						id: CONTENT_REVIEWER_NUDGE_ID,
						title: formatMessage(contentReviewerNudgeI18n.title),
						secondaryTitle: formatMessage(contentReviewerNudgeI18n.secondaryTitle),
						icon: () => (
							<AutomationIcon
								size="small"
								label={formatMessage(contentReviewerNudgeI18n.iconLabel)}
							/>
						),
						handleType: HandleActionType.Skill,
						type: 'skill-action',
						onClick: () => {
							publishRovoChat({
								type: 'chat-new',
								source: ROVO_CHAT_SOURCE,
								openChat: true,
								openChatMode: 'sidebar',
								data: {
									dialogues: [],
									prompt: resolveDynamicConfigStringValue(CONTENT_REVIEWER_PROMPT_CONFIG),
									placeholderType: 'skill',
									resultAttributionContext: {
										launchSource: ROVO_CHAT_SOURCE,
									},
								},
							});
						},
						nudgeContext: {
							id: CONTENT_REVIEWER_NUDGE_ID,
							selfEligible: true,
							onAccept: handleLocalStorageUpdate,
							onDismiss: (reason) => {
								if (reason?.dismissedMethod === 'manual') {
									handleLocalStorageUpdate();
								}
							},
						},
					}
				: null,
		[formatMessage, handleLocalStorageUpdate, publishRovoChat, isEnabled],
	);
};
